<template>
  <loader :is-loading="isLoading" color="primary" :full-page="true">
    <v-flex>
      <notifications group="user-form-error" position="top center" />
      <v-card flat id="user-card" class="pa-3">
        <v-card-title id="employee-title" class="form-title">
          <span>{{ $t(`components.usersForm.edit.title`) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="forceClear">mdi-close</v-icon>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <span class="text-uppercase input-label">
                {{ $t(`components.usersForm.default.name`) }}
              </span>
            </v-col>

            <v-col cols="6">
              <v-text-field
                :placeholder="$t(`components.usersForm.default.givenName`)"
                :class="{ 'employee-field-error': $v.user.givenName.$invalid }"
                :value="user.givenName"
                class="employee-field"
                height="44"
                dense
                outlined
                clearable
                hide-details
                @input="formState.patchField('givenName', $event)"
              >
                <template v-if="$v.user.givenName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.givenName" />
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="6">
              <v-text-field
                :value="user.familyName"
                :class="{ 'employee-field-error': $v.user.familyName.$invalid }"
                :placeholder="$t(`components.usersForm.default.familyName`)"
                class="employee-field"
                height="44"
                outlined
                clearable
                hide-details
                no-filter
                dense
                @input="formState.patchField('familyName', $event)"
              >
                <template v-if="$v.user.familyName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.familyName" />
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.email`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-text-field
                :value="user.email"
                :placeholder="$t(`components.usersForm.default.emailPlaceholder`)"
                :class="{ 'employee-field-error': $v.user.email.$invalid }"
                class="employee-field email-input"
                height="44"
                outlined
                clearable
                hide-details
                no-filter
                dense
                @input="formState.patchField('email', $event)"
              >
                <template v-if="$v.user.email.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.email" />
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.commLang`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :items="languageList"
                :value="user.communicationLanguage"
                class="employee-field"
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                item-value="value"
                outlined
                hide-details
                dense
                height="44"
                background-color="#f8f8f8"
                :class="{ 'input-field-error': $v.user.communicationLanguage.$invalid }"
                @change="formState.patchField('communicationLanguage', $event)"
              >
                <template v-if="$v.user.communicationLanguage.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.communicationLanguage" />
                </template>
              </v-select>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.role`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :items="roles"
                :value="user.role"
                class="employee-field"
                item-text="name"
                item-value="value"
                :menu-props="{ bottom: true, offsetY: true }"
                :class="{ 'input-field-error': $v.user.role.$invalid }"
                outlined
                hide-details
                dense
                :disabled="true"
                height="44"
                @change="formState.patchField('role', $event)"
              >
                <template v-if="$v.user.role.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.role" />
                </template>
              </v-select>
            </v-col>

            <v-col v-if="isCorporationAdmin">
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.corporations`)
              }}</span>
            </v-col>

            <v-col cols="12" v-if="isCorporationAdmin">
              <v-autocomplete
                v-model="selectedCorporation"
                outlined
                filled
                icon="$icons_product_corporation"
                item-text="name"
                item-value="id"
                height="44"
                dense
                class="employee-field-autocomplete"
                :placeholder="$t('components.usersForm.default.corporations')"
                :items="allCorporations"
                hide-details
                return-object
                @change="onCorporationChange"
              />

              <notifications group="corporations-error" position="top center" />
            </v-col>

            <v-col v-if="isCorporationAdmin" cols="12" class="corporation-list-wrapper">
              <div
                v-for="role in user.roles"
                :key="role.corporationId"
                class="d-flex align-center justify-space-between corporation-item"
              >
                <div>
                  <p class="mb-1 text-body-1">
                    {{ getActiveCorporationById(role.corporationId).name }}
                  </p>
                  <span class="text-subtitle-2">{{
                    getActiveCorporationById(role.corporationId).code
                  }}</span>
                  <span class="text-subtitle-2">
                    - {{ getActiveCorporationById(role.corporationId).contactEmail }}</span
                  >
                </div>

                <v-btn
                  @click="removeCorporation(role.corporationId)"
                  class="corporation-remove-btn"
                  icon
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="mt-4">
          <v-btn class="text-capitalize" color="grey darken-1" text @click="forceClear">
            {{ $t(`components.usersForm.edit.cancel`) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="text-capitalize white--text" color="#333333" depressed :disabled="isFormInvalid" @click="submitForm">
            {{ $t(`components.usersForm.edit.agree`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </loader>
</template>

<script>
import userValidationData from "@/helpers/rules/userValidationData";
import ErrorTooltip from "@/components/elements/ErrorTooltip.vue";

import { CHANNEL_ADMIN, CORP_ADMIN } from "@/helpers/userRoles";
import { languageMethods, languageList } from "@/mixins/language.mixins";

import { userState, loadUserById, transformerForUserRoles } from "./user.mixin";

export default {
  name: "EditUserForm",
  components: {
    ErrorTooltip,
  },
  mixins: [languageMethods, languageList],
  props: {
    userId: {
      type: String,
      required: true,
    },
    allCorporations: {
      type: Array,
      required: true,
    },
  },
  async created() {
    this.isLoading = true;
    const { data } = await loadUserById({ id: this.userId });

    this.formState = userState({
      ...data,
      roles: data.roles || [],
    });
    this.isLoading = false;
  },
  data() {
    return {
      formState: null,
      isLoading: false,
      corporations: [],
      corporationsLoading: false,
      selectedCorporation: null,
      roles: [
        { name: this.$t("components.usersForm.default.corpoAdmin"), value: CORP_ADMIN },
        { name: this.$t("components.usersForm.default.channelAdmin"), value: CHANNEL_ADMIN },
      ],
    };
  },
  computed: {
    isCorporationAdmin() {
      if (!this.formState) {
        return false;
      }

      return this.formState.state.role === CORP_ADMIN;
    },
    user() {
      if (!this.formState) {
        return {};
      }

      return this.formState.state;
    },
    isFormInvalid() {
      return this.$v.user.$invalid;
    }
  },
  methods: {
    forceClear() {
      this.formState.resetState();
      this.$emit("closeDialog");
    },
    async submitForm() {
      if (this.isFormInvalid) {
        this.$v.$touch();

        this.$notify({
          group: "user-form-error",
          type: "error",
          title: "Error",
          text: this.$t("components.usersForm.validation.error"),
        });
        return;
      }

      const url = 'users/updateUser';

      this.isLoading = true;

      const result = await this.$store.dispatch(url, {
        ...this.user,
        roles: transformerForUserRoles(this.user.role, this.user.roles),
      });

      if (result) {
        this.$notify({
          group: "user-form-success",
          type: "success",
          title: "Success",
          text: this.$t("components.bookingForm.validation.user-patch-success"),
        });

        this.forceClear();
      } else {
        this.$notify({
          group: "user-form-error",
          type: "error",
          title: "Error",
          text: this.$t("components.bookingForm.validation.user-patch-error"),
        });
      }

      this.isLoading = false;
    },
    getActiveCorporationById(id) {
      const corporation = this.allCorporations.find((item) => item.id === id);

      if (!corporation) {
        return {};
      }

      return corporation;
    },
    onCorporationChange(val) {
      if (this.formState.isRoleAdded(val.id)) {
        this.$notify({
          group: "corporations-error",
          type: "error",
          title: "Error",
          text: this.$t("components.bookingForm.validation.corporationAlreadyAdded"),
        });
        return;
      }

      if (val) {
        this.formState.patchField("roles", { name: val.name, corporationId: val.id });
      }

      this.selectedCorporation = null;
    },
    removeCorporation(id) {
      this.formState.removeRole(id);
    },
  },
  validations: {
    ...userValidationData,
  },
};
</script>

<style lang="scss" scoped>
#user-card {
  .input-label {
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888888;
  }

  .employee-field {
    background: #f8f8f8;
    box-sizing: border-box;
    border-radius: 6px;
    font-style: normal;
    line-height: 15px;
    border: 1px solid #eeeeee;
    letter-spacing: -0.005em;
    color: #333333;

    & fieldset {
      box-shadow: none !important;
    }
  }

  .employee-field:focus-within {
    border-color: #47d061;
  }

  .employee-field-error {
    border: 1px solid #eb5757 !important;
  }

  .corporation-list-wrapper {
    max-height: 250px;
    overflow-y: auto;
  }

  .corporation-item {
    background: #f8f8f8;
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid #eeeeee;

    & p {
      color: #333333;
    }
  }
}
</style>
